import React from 'react'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import { Button, makeStyles, useMediaQuery } from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'

/**
 * The single exit affordance for every merchant-facing preview of a quote — a floating pill in the
 * bottom-left corner. Both presentation versions use it so the button does not move between them:
 * v1 renders it in QuoteNextPreviewDialog, v2 in QuotePreviewDialog (v2 used to put a "Back to
 * Estimate" bar at the top of the page instead — ENG-1505).
 *
 * `position: fixed` means both callers must render it in the parent document, never inside the
 * preview iframe. iOS ignores an iframe's CSS height and grows the frame to its content, so the
 * iframe's viewport becomes the whole presentation and the pill lands at the bottom of the content
 * instead of the screen (ENG-1715).
 *
 * On a phone it is the prototype's floating 40px outlined circle with a back arrow and no label, lifted
 * clear of the iOS home indicator (ENG-2075).
 */
const useStyles = makeStyles(
  (theme: Theme) => ({
    root: {
      position: 'fixed',
      left: theme.spacing(2),
      bottom: theme.spacing(2),
      zIndex: 20,
      textTransform: 'none',
      borderRadius: theme.borderRadius.md,
      // The dark navy of the Item table header, not our blue: customers see this pill when the
      // estimator presents on the spot, and the blue clashes with a lot of company branding (ENG-2169).
      backgroundColor: theme.paintscout.palette.brand.Neutral.P35,
      color: theme.paintscout.palette.brand.Neutral.White,
      '&:hover': {
        boxShadow: theme.shadows[6],
        backgroundColor: theme.paintscout.palette.brand.Neutral.P35
      },
      '@media print': {
        display: 'none'
      }
    },
    circle: {
      position: 'fixed',
      left: 20,
      bottom: 'calc(20px + env(safe-area-inset-bottom))',
      zIndex: 20,
      width: 40,
      height: 40,
      padding: 0,
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#fcfcfd',
      border: `0.9px solid ${theme.paintscout.palette.brand.Neutral.P85}`,
      borderRadius: '50%',
      boxShadow: '0 4px 16px rgba(20, 30, 50, 0.12)',
      cursor: 'pointer',
      transition: 'background-color 0.12s',
      WebkitTapHighlightColor: 'transparent',
      '&:hover, &:active': {
        backgroundColor: theme.paintscout.palette.brand.Neutral.P96
      },
      '@media print': {
        display: 'none'
      }
    },
    arrow: {
      width: 16,
      height: 16,
      display: 'block'
    }
  }),
  { name: 'ExitPreviewButton' }
)

export interface ExitPreviewButtonProps {
  classes?: StyleClasses<typeof useStyles>
  onClick: (ev?: React.MouseEvent) => void
}

function ExitPreviewButton(props: ExitPreviewButtonProps) {
  const classes = useStyles(props)
  const { onClick } = props
  const isPhone = useMediaQuery('xs')

  if (isPhone) {
    return (
      <button type="button" aria-label="Exit Preview" className={classes.circle} onClick={onClick}>
        {/* the prototype's arrow-left glyph */}
        <svg className={classes.arrow} viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path
            d="M2.64481 8.02122C2.42699 8.23905 2.42699 8.59127 2.64481 8.80678L6.53784 12.7021C6.75566 12.9199 7.10789 12.9199 7.32339 12.7021C7.5389 12.4843 7.54122 12.1321 7.32339 11.9166L4.38045 8.97131H13.7909C14.0991 8.97131 14.3471 8.72336 14.3471 8.41516C14.3471 8.10696 14.0991 7.85901 13.7909 7.85901H4.38045L7.32571 4.91607C7.54353 4.69825 7.54353 4.34602 7.32571 4.13052C7.10789 3.91501 6.75566 3.91269 6.54015 4.13052L2.64481 8.02122Z"
            fill="#55647C"
          />
        </svg>
      </button>
    )
  }

  return (
    <Button variant="contained" className={classes.root} startIcon={<ArrowBackIcon />} onClick={onClick}>
      Exit Preview
    </Button>
  )
}

export default ExitPreviewButton
